import classNames from 'classnames';
import { isString } from 'lodash';
import { memo, useCallback } from 'react';

import { Copy } from '@air/next-icons';

import { formatJSON } from '../utils';

type DataSectionProps = {
  title: string;
  data: unknown;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onCopy: (text: string) => void;
  copyTitle: string;
};

export const DataSection = memo(
  ({ title, data, isExpanded, onToggleExpanded, onCopy, copyTitle }: DataSectionProps) => {
    const handleCopy = useCallback(() => {
      const textToCopy = isString(data) ? data : JSON.stringify(data, null, 2);
      onCopy(textToCopy);
    }, [data, onCopy]);

    return (
      <section className="flex min-h-0 flex-1 flex-col">
        <div className="mb-2 flex items-center justify-between">
          <button
            type="button"
            onClick={onToggleExpanded}
            className="mb-2 flex items-center gap-2 font-medium text-12 text-grey-12 hover:text-grey-12"
          >
            <span className={classNames('transition-transform', isExpanded ? 'rotate-90' : '')}>▶</span>
            {title}
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className="rounded p-1 text-grey-9 transition-colors hover:bg-grey-2 hover:text-grey-11"
            title={copyTitle}
          >
            <Copy className="size-4" />
          </button>
        </div>
        {isExpanded && (
          <div className="h-[300px] overflow-y-auto rounded-12 border border-grey-3 bg-grey-1 shadow-sm">
            <div className="p-3">
              <div className="whitespace-pre-wrap break-words bg-transparent font-mono text-12 leading-relaxed">
                {formatJSON(data)}
              </div>
            </div>
          </div>
        )}
      </section>
    );
  },
);

DataSection.displayName = 'DataSection';
